import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { z } from "zod";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";

const emailSchema = z.string().email();

const ForgotPassword = () => {
  const { t } = useTranslation();
  const { resetPassword } = useAuth();
  
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setError(t("auth.errors.invalidEmail"));
      return;
    }

    setLoading(true);
    const { error: resetError } = await resetPassword(email);
    setLoading(false);

    if (resetError) {
      toast.error(resetError.message);
    } else {
      setSent(true);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-[hsl(var(--accent-yellow))] rounded-2xl border-2 border-foreground p-8 text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4" />
            <h1 className="heading-card mb-4">{t("auth.resetEmailSent")}</h1>
            <p className="text-foreground/80 mb-6">{t("auth.resetEmailSentDesc")}</p>
            <Link to="/login" className="btn-primary inline-flex">
              <ArrowLeft className="w-4 h-4" />
              {t("auth.backToLogin")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-[hsl(var(--accent-yellow))] rounded-2xl border-2 border-foreground p-8">
          <Link to="/" className="logo block text-center mb-6">
            Vesper
          </Link>
          
          <div className="text-center mb-8">
            <Mail className="w-12 h-12 mx-auto mb-4" />
            <h1 className="heading-card mb-2">{t("auth.forgotPassword")}</h1>
            <p className="text-foreground/80 text-sm">{t("auth.forgotPasswordDesc")}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t("auth.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-full border-2 border-foreground bg-background text-foreground placeholder:text-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={t("auth.emailPlaceholder")}
              />
              {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary justify-center disabled:opacity-50"
            >
              {loading ? t("auth.loading") : t("auth.sendResetLink")}
            </button>
          </form>

          <p className="text-center mt-6 text-sm">
            <Link to="/login" className="font-semibold hover:underline inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              {t("auth.backToLogin")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
